import React, { useEffect, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { useSelector } from 'react-redux';

import { CompareResults } from '../../CompareResults';

import { SearchSummary } from '../ComparisonSummary/SearchSummary';

import { LoanTermTabs } from '../../FinancingSection/LoanTermTabs';

import { Footer } from '../../Footer/Footer';

import { AUTO_OR_MORTGAGE_LOAN_TERMS } from '../../../constants/Dashboard/Financing';

import {
  MAKE,
  MODEL,
  YEAR,
  COLOR,
  AUTO_TYPE,
  MILEAGE,
  DOWN_PAYMENT,
  VEHICLE_PRICE,
  TRADE_IN_VALUE,
  LOAN_AMOUNT,
  HOME_PRICE,
  PROPERTY_TYPE,
  OCCUPANCY_TYPE,
  HOME,
  AUTO,
} from '../../../constants/ComparisonSummary';

import {
  TERM_MONTHS_AMORTIZATION_SCHEDULE,
  borderAndBackgroundColor,
  LOAN_RATE_TERM_MONTH_CHECK,
  FEDERAL_CREDIT_UNION,
  COMMUNITY_CREDIT_UNION,
  EMPLOYEES_CREDIT_UNION,
  GOUVERNEUR_SAVINGS,
  FCU,
  CCU,
  ECU,
  GSL,
  INTEREST_PAID,
  PRINCIPAL_PAID,
  FIVE,
  THREE,
} from '../../../constants/RatesAnalytics';

import { LENDERS_LOGO_OBJ } from '../Logos';

import './index.css';

export const RatesAnalytics = ({ user }) => {
  const [financeLoanTerms, setFinanceLoanTerms] = useState({});
  const [selectedLoanTerms, setSelectedLoanTerms] = useState({});

  const { financingType, ratesReducer, ...vehOrPropertyProps } = useSelector(
    ({
      vehicleReducer: {
        auto_type,
        color,
        downPayment,
        estimateValue,
        financingType,
        make,
        mileage,
        model,
        selectedCreditTier,
        selectedLoanTerms,
        tradeInValue,
        year,
      },
      propertyReducer,
      ratesReducer,
    }) => ({
      financingType: financingType || propertyReducer.financingType,
      [AUTO]: {
        [AUTO_TYPE]: auto_type,
        [COLOR]: color,
        [DOWN_PAYMENT]: downPayment,
        [VEHICLE_PRICE]: estimateValue,
        estimateValue,
        [MAKE]: make,
        [MILEAGE]: mileage,
        [MODEL]: model,
        selectedCreditTier,
        selectedLoanTerms,
        [TRADE_IN_VALUE]: tradeInValue,
        [YEAR]: year,
        [LOAN_AMOUNT]: Number(estimateValue) - (Number(downPayment) + Number(tradeInValue)),
      },
      [HOME]: {
        [DOWN_PAYMENT]: propertyReducer.downPayment,
        [HOME_PRICE]: propertyReducer.estimateValue,
        estimateValue,
        [TRADE_IN_VALUE]: propertyReducer.tradeInValue,
        [PROPERTY_TYPE]: propertyReducer.propertyType,
        [OCCUPANCY_TYPE]: propertyReducer.occupancyType,
        [LOAN_AMOUNT]:
          Number(propertyReducer.estimateValue) -
          (Number(propertyReducer.downPayment) + Number(propertyReducer.tradeInValue)),
        selectedLoanTerms: propertyReducer.mortgageTermInYears,
        selectedLoanTermInMonths: propertyReducer.mortgageTerm,
        selectedCreditTier,
      },
      ratesReducer,
    }),
  );

  const objRatesKey = financingType === AUTO ? 'finance_auto_rates' : 'finance_mortgage_rates';
  const objLoanTermMonthKey = financingType === AUTO ? 'loanTermMonth' : 'mortgageLoanTermMonth';
  const multiplyByLoanTermMonth = financingType === AUTO ? 1 : 12; // convert years to months for mortgages

  useEffect(() => {
    const obj = {};
    Object.keys(vehOrPropertyProps[financingType]?.selectedLoanTerms || obj)?.forEach((term) => {
      obj[term] = AUTO_OR_MORTGAGE_LOAN_TERMS?.[financingType]?.[term];
    });

    setFinanceLoanTerms(obj);

    setSelectedLoanTerms((prevState) => {
      if (typeof vehOrPropertyProps[financingType]?.selectedLoanTerms !== 'object') {
        return prevState;
      }
      return {
        [Object.keys(vehOrPropertyProps[financingType]?.selectedLoanTerms)[0]]: Object.keys(
          vehOrPropertyProps[financingType]?.selectedLoanTerms,
        )[0],
      };
    });
  }, []);

  const lendersArr = Object.keys(ratesReducer?.lenders || {});
  const lendersRatesArr =
    lendersArr
      ?.reduce((acc, curr) => {
        const lenderRates = ratesReducer?.lenders?.[curr];
        const foundLoanTerm = lenderRates.find((loanRate) => {
          return (
            LOAN_RATE_TERM_MONTH_CHECK[financingType](
              loanRate?.[objRatesKey]?.[objLoanTermMonthKey],
            ) === Number(Object.keys(selectedLoanTerms)?.[0])
          );
        });

        if (foundLoanTerm) {
          return [...acc, foundLoanTerm];
        }

        return [...acc];
      }, [])
      ?.sort((a, b) => a?.[objRatesKey].updatedApr - b?.[objRatesKey]?.updatedApr) || [];

  if (lendersRatesArr?.length > FIVE) {
    lendersRatesArr.length = FIVE;
  }

  const scheduleData = lendersRatesArr?.map((lender) => {
    const loanTermMonth = Object.keys(selectedLoanTerms || {})?.[0] * multiplyByLoanTermMonth;
    const LoanTermPaymentSchedule = Object.keys(
      TERM_MONTHS_AMORTIZATION_SCHEDULE[Number(loanTermMonth)] || {},
    );

    return LoanTermPaymentSchedule.map((loanTerm) => {
      return (
        Number(loanTerm) *
        multiplyByLoanTermMonth *
        Math.round(Number(lender[objRatesKey]?.amortizationInfo?.monthlyPayment))
      );
    });
  });

  const copyLendersRatesArr =
    lendersRatesArr?.length > THREE
      ? lendersRatesArr?.filter(
          (_, index) => index === 0 || index === 2 || index === lendersRatesArr.length - 1,
        )
      : lendersRatesArr;

  const TOTAL_AMOUNT_PAID_OVER_TIME = {
    labels: Object.keys(
      TERM_MONTHS_AMORTIZATION_SCHEDULE?.[
        Number(Object.keys(selectedLoanTerms)?.[0]) * multiplyByLoanTermMonth
      ] || {},
    ).map((el) => 'Year' + ' ' + el),
    datasets: copyLendersRatesArr.map(({ finance_lenders }, index) => {
      return {
        label: finance_lenders.name
          .replace(FEDERAL_CREDIT_UNION, FCU)
          .replace(COMMUNITY_CREDIT_UNION, CCU)
          .replace(EMPLOYEES_CREDIT_UNION, ECU)
          .replace(GOUVERNEUR_SAVINGS, GSL),
        data: scheduleData[index],
        borderColor: borderAndBackgroundColor?.[index]?.borderColor || 'blue',
        backgroundColor: borderAndBackgroundColor?.[index]?.backgroundColor || 'black',
        fill: false,
      };
    }),
  };

  const INTEREST_RATE_COMPARISON_DATA = {
    labels: lendersRatesArr?.map((_, index) => 'Lender ' + (index + 1)),
    datasets: [
      {
        label: 'INTEREST_RATE',
        data: lendersRatesArr?.map((el) => el?.[objRatesKey]?.updatedApr),
        backgroundColor: [
          'rgba(255, 99, 132, .7)',
          'rgba(54, 162, 235, .7)',
          'rgba(255, 206, 86, .7)',
          'rgba(75, 192, 192, .7)',
          'rgba(30, 100, 100, .7)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(30, 100, 100, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const legend = lendersRatesArr?.map((lender) => {
    return (
      <a
        href={lender?.[objRatesKey]?.url}
        target="_blank"
        onClick={() => {
          mixpanel.track(`Rates Info ${financingType}`, {
            lender_name: lender?.['finance_lenders']?.name,
            url: lender?.[objRatesKey]?.url,
            path: '/analytics',
          });
        }}
      >
        <img src={LENDERS_LOGO_OBJ[lender?.['finance_lenders']?.name]} height="40" width="80" />
      </a>
    );
  });

  return (
    <div>
      <SearchSummary
        isLoading={ratesReducer.loading}
        financingType={financingType}
        user={user}
        {...vehOrPropertyProps?.[financingType]}
      />
      <div className="analytics-loan-term-tabs-wrapper">
        <LoanTermTabs
          selectedLoanTerms={selectedLoanTerms}
          handleLoanTermClick={(loanTerm) => {
            setSelectedLoanTerms({ [loanTerm]: loanTerm });
            mixpanel.track(`Rates Info ${financingType}`, {
              path: '/analytics',
              [loanTerm]: loanTerm,
              financingType,
              vehOrPropertyProps: { ...vehOrPropertyProps[financingType] },
            });
          }}
          financeLoanTerms={financeLoanTerms}
        />
      </div>
      <CompareResults
        interest_rate_comparison={INTEREST_RATE_COMPARISON_DATA}
        overall_payment_overtime={TOTAL_AMOUNT_PAID_OVER_TIME}
        loanTerm={selectedLoanTerms}
        legend={legend}
        className="analytics-comparison-item-header"
      />
      <div className="mobile-view-only">
        <Footer currentPath={'/analytics'} />
      </div>
    </div>
  );
};
